import { useLayoutEffect } from "react";

// Below this the list would be too short to use, so on a very short window the
// dropdown may run past the bottom rather than shrink further.
const MIN_HEIGHT = 120;

// Caps an element's max-height at the room left between its top and the bottom
// of the window, so a part of it can scroll instead of the whole thing running
// off-screen. Built for the label lists in the search page's dropdowns (the
// Label menus, My properties), which open below a toolbar on pages that do not
// scroll: with many labels, "New label +", "Add Label to selected properties"
// and Clear / Apply ended up below the window, reachable only by zooming the
// browser out. Re-measured after every render (the dropdown's content and
// position change with it) and on window resize.
//
// options:
//   bottomGap     space to keep free below (px), default 40
//   containerRef  the dropdown the element sits in; whatever the dropdown has
//                 below the element (its buttons, padding) is kept on screen too
//   containerSelector  the same, found as the element's closest() ancestor, for
//                 components that don't own their dropdown (filters-tabs/labels)
//   active        measure only while true (e.g. while the dropdown is open)
const useFitToViewport = (ref, options = {}) => {
  const { bottomGap = 40, containerRef = null, containerSelector = null, active = true } = options;
  const containerOf = (el) =>
    containerRef?.current ?? (containerSelector && el ? el.closest(containerSelector) : null);

  useLayoutEffect(() => {
    if (active) fit(ref.current, containerOf(ref.current), bottomGap);
  });

  useLayoutEffect(() => {
    if (!active) return undefined;
    let settle;
    const refit = () => fit(ref.current, containerOf(ref.current), bottomGap);
    const onResize = () => {
      refit();
      // Again once the page has settled: the toolbars above the menu re-wrap
      // after the resize event (their offsets come from ResizeObservers), which
      // moves the menu after the first measurement.
      clearTimeout(settle);
      settle = setTimeout(refit, 150);
    };
    window.addEventListener("resize", onResize);
    return () => {
      clearTimeout(settle);
      window.removeEventListener("resize", onResize);
    };
    // containerOf only reads the two options listed here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, containerRef, containerSelector, bottomGap, active]);
};

function fit(el, container, bottomGap) {
  if (!el) return;
  const rect = el.getBoundingClientRect();
  // Not laid out (display: none, e.g. the toolbar copy for the other screen size).
  if (rect.width === 0 && rect.height === 0) return;
  // What the dropdown shows below this element doesn't depend on its height.
  const below = container ? Math.max(container.getBoundingClientRect().bottom - rect.bottom, 0) : 0;
  const height = `${Math.max(window.innerHeight - rect.top - below - bottomGap, MIN_HEIGHT)}px`;
  el.style.maxHeight = height;
  // For CSS that must not outgrow the same space (the Label menu's popups).
  el.style.setProperty("--fit-viewport-height", height);
}

export default useFitToViewport;
